import { useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { LayoutOrderContainer } from './orders/_layouts/LayoutOrderContainer';
import {
  ProductOptimizationProvider,
  useProductOptimization,
} from './ProductOptimization/ProductOptimizationContext';
import SearchBar from './ProductOptimization/SearchBar';
import UsageBanner from './ProductOptimization/UsageBanner';
import DiagnosisList from './ProductOptimization/DiagnosisList';
import DiagnosisDetailPanel from './ProductOptimization/DiagnosisDetailPanel';
import { TabSection } from './ProductOptimization/TabSection';
import { useCompletedToast } from './ProductOptimization/useCompletedToast';

const ErrorFallback = ({ resetError }: { resetError: () => void }) => (
  <div className="flex min-h-[360px] flex-col items-center justify-center gap-[12px] bg-gr-50 px-[20px] py-[60px]">
    <div className="text-gr-900 Title3S18">일시적인 오류가 발생했어요</div>
    <div className="text-gr-600 Body5M14">잠시 후 다시 시도하거나 페이지를 새로고침해주세요.</div>
    <button
      type="button"
      onClick={resetError}
      className="mt-[8px] rounded-[8px] border border-gr-300 bg-white px-[14px] py-[8px] text-gr-700 Body5M14 hover:bg-gr-100"
    >
      다시 시도
    </button>
  </div>
);

const ProductOptimizationPage = () => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}
    >
      <ProductOptimizationProvider>
        <ProductOptimizationContent />
      </ProductOptimizationProvider>
    </Sentry.ErrorBoundary>
  );
};

export default ProductOptimizationPage;

const ProductOptimizationContent = () => {
  const [searchParams] = useSearchParams();
  const selectedId = searchParams.get('select');
  const { diagnoses, usage, isError, refetchList, refetchUsage } = useProductOptimization();

  useCompletedToast(diagnoses);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.querySelectorAll<HTMLElement>('.scrollbar').forEach((el) => {
      el.scrollTo(0, 0);
    });
  }, [selectedId]);

  if (selectedId) {
    return <DiagnosisDetailPanel />;
  }

  return (
    <LayoutOrderContainer className="items-center bg-gr-50 px-[60px]">
      <div className="flex w-full flex-col bg-gr-50 pt-[40px]">
        <div className="flex w-full flex-col items-center justify-center">
          <TabSection />
        </div>
      </div>

      <SearchBar />

      <div className="flex h-fit w-full max-w-full flex-col gap-[16px] bg-gr-50 py-[24px] pt-[6px]">
        <UsageBanner usage={usage} />
        {isError && (
          <div className="flex items-center justify-between rounded-[12px] border border-statusRed-400 bg-statusRed-100 px-[16px] py-[12px] text-statusRed-400 Body5M14">
            <span>진단 정보를 불러오지 못했어요. 네트워크 상태를 확인하거나 잠시 후 다시 시도해주세요.</span>
            <button
              type="button"
              onClick={() => {
                refetchList();
                refetchUsage();
              }}
              className="ml-[12px] shrink-0 rounded-[8px] border border-statusRed-400 bg-white px-[10px] py-[6px] text-statusRed-400 Body5M14 hover:bg-statusRed-100"
            >
              다시 시도
            </button>
          </div>
        )}
        <DiagnosisList />
      </div>
    </LayoutOrderContainer>
  );
};
